import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

export type AiVersion = { proposalId: Id<"takeoffPilotAiProposals">; version: number };
export type AiVersionChange = AiVersion & { beforeVersion: number };
type Cursor = { proposalId: AiVersion["proposalId"]; version: number };

/** Holds only versions observed by the user or returned by this client's own edits. */
export class AiHistoryLineage {
  private readonly cursors = new Map<AiVersion["proposalId"], Cursor>();

  track(versions: AiVersion[]): Cursor[] {
    return versions.map(({ proposalId, version }) => {
      let cursor = this.cursors.get(proposalId);
      // A newer observation starts a separate lineage. Old history retains its
      // own cursor and cannot silently adopt an intervening external edit.
      if (!cursor || version > cursor.version) {
        cursor = { proposalId, version };
        this.cursors.set(proposalId, cursor);
      }
      return cursor;
    });
  }

  expected(cursors: Cursor[]): AiVersion[] {
    return cursors.map(({ proposalId, version }) => ({ proposalId, version }));
  }

  advance(cursors: Cursor[], changes: AiVersionChange[]) {
    for (const change of changes) {
      const cursor = cursors.find((entry) => entry.proposalId === change.proposalId);
      if (!cursor || cursor.version !== change.beforeVersion) {
        throw new Error("AI history version did not match the completed edit");
      }
      cursor.version = change.version;
    }
  }
}
